import { initializeApp } from "firebase/app"
import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
  onAuthStateChanged,
} from "firebase/auth"
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)

const googleProvider = new GoogleAuthProvider()

export const authHelpers = {
  getSession: () => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe()
        resolve({ session: user ? { user } : null, error: null })
      })
    })
  },

    signIn: async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      const user = result.user

      // Self-heal: learners who signed up before users/{uid} docs existed
      // get one created here, from whatever Firebase Auth already has for
      // free. Name still can't be recovered — it was never captured — but
      // email/avatar can, and that's what unblocks the enrollment backfill
      // in touchEnrollmentActivity.
      const userRef = doc(db, "users", user.uid)
      const existing = await getDoc(userRef)
      if (!existing.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email || "",
          name: "",
          avatarUrl: user.photoURL || "",
          accountType: "individual",
          createdAt: serverTimestamp(),
        }).catch(() => {})
      }

      return { user, error: null }
    } catch (error) {
      return { user: null, error }
    }
  },

    signUp: async (email, password, name) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      const user = result.user

      // Individual learners have no users/{uid} doc until now — write it here,
      // mirroring the shape registerOrganization() writes for org owners.
      await setDoc(
        doc(db, "users", user.uid),
        {
          uid: user.uid,
          email,
          name: name || "",
          avatarUrl: "",
          accountType: "individual",
          createdAt: serverTimestamp(),
        },
        { merge: true }
      ).catch(() => {})

      return { user, error: null }
    } catch (error) {
      return { user: null, error }
    }
  },

    signInWithGoogle: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      const userRef = doc(db, "users", user.uid)
      const existing = await getDoc(userRef)
      if (!existing.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email || "",
          name: user.displayName || "",
          avatarUrl: user.photoURL || "",
          accountType: "individual",
          createdAt: serverTimestamp(),
        }).catch(() => {})
      }

      return { user, error: null }
    } catch (error) {
      return { user: null, error }
    }
  },

  signOut: async () => {
    try {
      await signOut(auth)
      return { error: null }
    } catch (error) {
      return { error }
    }
  },

  onAuthStateChanged: (callback) => {
    return onAuthStateChanged(auth, callback)
  },

  // Re-prompts for the current password before a sensitive action.
  // Firebase requires a "recent" login for actions like this in principle,
  // but since we don't force re-login often, we do it explicitly and deliberately here.
  reauthenticate: async (password) => {
    try {
      const currentUser = auth.currentUser
      if (!currentUser?.email) {
        return { error: { message: "No signed-in user found." } }
      }
      const credential = EmailAuthProvider.credential(currentUser.email, password)
      await reauthenticateWithCredential(currentUser, credential)
      return { error: null }
    } catch (error) {
      return { error }
    }
  }
}
